import { FILL_RING_COLOR, PORT_TYPE_COLOR } from "../constants";
import { isKitTimbre } from "../audio";
import { formatSig4, openRelativePopover, reattachRelativeIfFor } from "./numPopover";
import { Edge, PortId, PORTS } from "../connections";
import { ObjectType, ValueOperatorKind, valueOperatorKind } from "../objects";
import { expressionMemberOrder } from "../exprBuilder";
import { AppContext, NumericParam, Tool } from "../appContext";
import { PanelLayout } from "./panelLayout";
import { MessageKey, t } from "../i18n";
import { paramLabel } from "../paramLabels";
import { isNodeGatedTool } from "../featureGate";

interface PortsDeps {
  objectPanelEl: HTMLElement | null;
  mainPanelEl: HTMLElement | null;
  pushUndo(): void;
  popUndo(): void;
  persistConnections(): void;
  onConnect(toPort: PortId): void;
  bindPanel(): void;
  settableParams(id: string): NumericParam[];
  offeredParams(id: string): string[];
  setNodeOperand(id: string, value: number): void;
  highlightWireTarget(id: string | null): void;
  nodeUiVisible(): boolean;
  hitTestIds(ev: PointerEvent): string[];
  entityLabel(id: string): string;
  createEntityAt(tool: Tool, clientX: number, clientY: number): string | null;
  isClaimLocked(id: string): boolean;
  wireGuestLocked(id: string): boolean;
}

export interface PortsApi {
  renderMainPorts(): void;
  updateConnectionOverlay(): void;
  cancelPending(): void;
  canTargetAcceptPending(targetId: string): boolean;
  updateWireHover(acceptableIds: string[]): void;
  focusWireTarget(targetId: string): void;
  settlePending(acceptableIds: string[]): boolean;
  pendingId(): string | null;
  pendingPort(): PortId | null;
  isPending(): boolean;
}

type LineEnd = { x: number; y: number; cx?: number; cy?: number };

type ObjectEntry = { x: number; y: number; cx: number; cy: number };

/**
 * Node-port editor: per-entity port pins rendered as an on-object overlay
 * anchored to the selected entity's screen projection, the connection-mode
 * state machine (tap to start/complete a wire, long-press to disconnect), the
 * Param-in dropdown / op / operand controls, and the SVG connection overlay
 * (object↔object lines, color-keyed per linked node). Owns its pending/hover
 * state; the main-resident pointer handler drives it through the returned api.
 */
export function initPorts(ctx: AppContext, panel: PanelLayout, deps: PortsDeps): PortsApi {
  const { field, emitterField, graph, settings, gizmo } = ctx;
  const { projForNodeLive, projForNodeLiveRaised } = panel;
  const { objectPanelEl, pushUndo, popUndo, persistConnections, onConnect, bindPanel, settableParams, offeredParams, setNodeOperand, highlightWireTarget, nodeUiVisible, hitTestIds, entityLabel, createEntityAt, isClaimLocked, wireGuestLocked } = deps;

  const PORT_LABELS: Record<PortId, string> = {
    pulseOut: t("port.pulse"),
    emitIn: t("port.emit"),
    inc: t("port.pulse"),
    reset: t("port.reset"),
    chargeIn: t("port.charge"),
    valueOut: t("port.value"),
    valueIn: t("port.value"),
    operand1In: t("port.input1"),
    operand2In: t("port.input2"),
    transformOut: t("port.parent"),
    transformIn: t("port.child"),
    setParamIn: t("port.param"),
    delayIn: t("port.pulse"),
    flashIn: t("port.flash"),
    routeIn: t("port.pulse"),
    routeOut: t("port.out"),
    stepIn: t("port.pulse"),
    target: t("port.target"),
    envelopeIn: t("port.envelope"),
    envelopeOut: t("port.envelope"),
    pitchQuantizeIn: t("port.pitchQuantize"),
    pitchQuantizeOut: t("port.pitchQuantize"),
  };

  const NODE_TYPE_LABEL: Record<string, string> = {
    box: t("node.box"),
    sphere: t("node.sphere"),
    funnel: t("node.funnel"),
    force: t("node.force"),
    value: t("node.value"),
    timer: t("node.timer"),
    decay: t("node.decay"),
    orbit: t("node.orbit"),
    group: t("node.group"),
    timbre: t("node.timbre"),
    pitchQuantize: t("node.pitchQuantize"),
    global: t("node.global"),
    router: t("node.router"),
    emitter: t("node.emitter"),
    button: t("node.button"),
    lamp: t("node.lamp"),
    knob: t("node.knob"),
    envelope: t("node.envelope"),
    array: t("node.array"),
    curve: t("node.curve"),
  };

  const LONG_PRESS_MS = 450;
  const DRIFT_PX = 10;
  const PORTS_OVERLAY_BASE_CLEARANCE_PX = 6;
  const PORTS_OVERLAY_RING_OVERLAP_PX = 14;

  /**
   * Single-connection ports render one lone pin (no growth): value-in, operand-in
   * and transform-in each accept one source. Every other port follows the UE idiom
   * of one 1:1 pin per wire plus a trailing empty pin that grows a new one —
   * including outputs (fan-out becomes multiple out pins) and the fan-in Param-in.
   */
  function isSinglePort(port: PortId): boolean {
    return port === "operand1In" || port === "operand2In" || port === "transformIn" || port === "target" || port === "envelopeIn" || port === "pitchQuantizeIn";
  }

  function entityType(id: string): ObjectType | "emitter" | null {
    for (const o of field.list()) {
      if (o.id === id) {
        return o.type;
      }
    }
    for (const e of emitterField.list()) {
      if (e.id === id) {
        return "emitter";
      }
    }
    return null;
  }

  /**
   * Display label of a pin on a given entity: a timer's valueOut reads as its
   * mode ("time" = elapsed seconds, "loop" = performance-cycle phase); every
   * other pin keeps the generic per-port label.
   */
  function portLabel(entityId: string, port: PortId): string {
    if (port === "valueOut" && entityType(entityId) === "timer") {
      const obj = field.list().find((o): boolean => o.id === entityId);
      return obj ? obj.timerMode : PORT_LABELS[port];
    }
    if (port === "reset" && entityType(entityId) === "global") {
      return t("port.clockReset");
    }
    if (port === "inc" && entityType(entityId) === "count") {
      return t("port.count");
    }
    return PORT_LABELS[port];
  }

  function entityName(id: string): string {
    for (const o of field.list()) {
      if (o.id === id) {
        return o.name;
      }
    }
    for (const e of emitterField.list()) {
      if (e.id === id) {
        return e.name;
      }
    }
    return "";
  }

  /**
   * The port set a given entity type exposes. Calc is the only type whose ports
   * depend on instance state (its operator kind selects operand / pulse pins), so
   * a fresh-type query (candidate computation) passes the default binary kind
   * while portsForEntity resolves the live operator.
   */
  function portsForType(type: ObjectType | "emitter", calcKind: ValueOperatorKind = "binary"): PortId[] {
    if (type === "emitter") {
      return ["emitIn", "transformIn", "setParamIn"];
    }
    if (type === "count") {
      return ["inc", "reset", "valueOut", "transformIn"];
    }
    if (type === "display") {
      return ["valueIn", "transformIn"];
    }
    if (type === "calc") {
      if (calcKind === "random") {
        return ["inc", "operand1In", "valueOut", "transformIn"];
      }
      if (calcKind === "binary") {
        return ["operand1In", "operand2In", "valueOut", "transformIn"];
      }
      return ["operand1In", "valueOut", "transformIn"];
    }
    if (type === "timer") {
      return ["valueOut", "transformIn"];
    }
    if (type === "decay") {
      return ["chargeIn", "valueOut", "transformIn"];
    }
    if (type === "delay") {
      return ["delayIn", "pulseOut", "transformIn"];
    }
    if (type === "router") {
      return ["routeIn", "routeOut", "transformIn"];
    }
    if (type === "button") {
      return ["valueOut", "pulseOut", "transformIn"];
    }
    if (type === "lamp") {
      return ["flashIn", "transformIn", "setParamIn"];
    }
    if (type === "knob") {
      return ["valueOut", "transformIn"];
    }
    if (type === "envelope") {
      return ["envelopeOut", "transformIn", "setParamIn"];
    }
    if (type === "array" || type === "curve") {
      return ["stepIn", "operand1In", "valueOut", "transformIn"];
    }
    if (type === "orbit") {
      return ["transformOut", "transformIn", "setParamIn", "target"];
    }
    if (type === "group") {
      return ["transformOut", "transformIn", "setParamIn"];
    }
    if (type === "global") {
      return ["reset", "setParamIn"];
    }
    if (type === "timbre") {
      return ["envelopeIn", "pitchQuantizeIn", "transformIn", "setParamIn"];
    }
    if (type === "pitchQuantize") {
      return ["pitchQuantizeOut", "transformIn"];
    }
    if (type === "box" || type === "sphere" || type === "funnel") {
      return ["pulseOut", "transformIn", "setParamIn"];
    }
    if (type === "force" || type === "rail") {
      return ["transformIn", "setParamIn"];
    }
    return [];
  }

  function portsForEntity(id: string): PortId[] {
    const ty = entityType(id);
    if (ty === null) {
      return [];
    }
    if (ty === "calc") {
      const obj = field.list().find((o): boolean => o.id === id);
      return portsForType("calc", obj ? valueOperatorKind(obj.operator) : "binary");
    }
    return portsForType(ty);
  }

  function groupInsideIds(groupId: string): Set<string> {
    const inside = new Set<string>([groupId]);
    const queue: string[] = [groupId];
    while (queue.length > 0) {
      const cur = queue.pop()!;
      for (const e of graph.edgesFrom(cur)) {
        if (e.fromPort === "transformOut" && !inside.has(e.to)) {
          inside.add(e.to);
          queue.push(e.to);
        }
      }
    }
    return inside;
  }

  /**
   * Edges crossing the group boundary: exactly one end lies in the group subtree.
   * The group's own parent-child edges never qualify (both ends inside). Dedupe
   * keys on the edge id — identical parallel edges (param fan-in runs two wires
   * between the same pins) are distinct wires and must all draw.
   */
  function groupBoundaryEdges(groupId: string): Edge[] {
    const inside = groupInsideIds(groupId);
    const seen = new Set<string>();
    const out: Edge[] = [];
    for (const id of inside) {
      for (const e of graph.edgesForEntity(id)) {
        if (inside.has(e.from) !== inside.has(e.to) && !seen.has(e.id)) {
          seen.add(e.id);
          out.push(e);
        }
      }
    }
    return out;
  }

  let pendingConnection: { id: string; port: PortId } | null = null;
  let wireFocusId: string | null = null;
  let wireHoverId: string | null = null;
  let wireDisplayId: string | null = null;
  const cursor = { x: 0, y: 0 };

  const SVG_NS = "http://www.w3.org/2000/svg";
  const overlay = document.createElementNS(SVG_NS, "svg");
  overlay.setAttribute("class", "connection-overlay");
  document.body.appendChild(overlay);

  const portsOverlay = document.createElement("div");
  portsOverlay.className = "ports-overlay ports-main-overlay";
  document.body.appendChild(portsOverlay);
  function appendPortColumnHeaders(container: HTMLElement): void {
    const inHead = document.createElement("div");
    inHead.className = "ports-column-title ports-column-title-in";
    inHead.textContent = t("ports.inputs");
    const outHead = document.createElement("div");
    outHead.className = "ports-column-title ports-column-title-out";
    outHead.textContent = t("ports.outputs");
    container.append(inHead, outHead);
  }


  const subOverlay = document.createElement("div");
  subOverlay.className = "ports-overlay ports-suboverlay";
  document.body.appendChild(subOverlay);

  const mainDots = new Map<string, HTMLElement>();
  const subDots = new Map<string, HTMLElement>();
  let subPanelId: string | null = null;

  function edgeOutKey(e: Edge): string {
    return `o|${e.id}`;
  }

  function edgeInKey(e: Edge): string {
    return `i|${e.id}`;
  }

  function emptyKey(dir: "o" | "i", id: string, port: PortId): string {
    return `${dir}-empty|${id}|${port}`;
  }

  /**
   * Screen anchor for a port dot's wire end. The dot lives in the scrollable
   * ports panel, so a dot scrolled past the 50vh cap reports an off-panel rect;
   * its y is clamped into the panel's visible band so the wire stays pinned to
   * the panel edge instead of trailing to a hidden position.
   */
  function dotAnchor(key: string, side: "l" | "r"): { x: number; y: number } | null {
    const el = mainDots.get(key);
    if (!el) {
      return null;
    }
    const r = el.getBoundingClientRect();
    const panel = portsOverlay.getBoundingClientRect();
    const y = Math.max(panel.top, Math.min(panel.bottom, r.top + r.height / 2));
    return { x: side === "r" ? r.right : r.left, y };
  }

  /**
   * Whether a new wire may start from this pin: outputs and growable inputs
   * always fan out into a fresh edge; a single-connection input only while
   * unwired. Gates the drift-to-wire gesture on connected pins.
   */
  function canStartWireFrom(entityId: string, port: PortId): boolean {
    return PORTS[port].dir === "out" || !isSinglePort(port) || graph.edgesTo(entityId, port).length === 0;
  }

  function acceptableTargets(ev: PointerEvent): string[] {
    if (!pendingConnection) {
      return [];
    }
    const src = pendingConnection.id;
    return hitTestIds(ev).filter((id: string): boolean => id !== src && canTargetAcceptPending(id));
  }

  /**
   * Wires one port dot with the unified press gesture: drifting past DRIFT_PX
   * starts a wire from the pin (the drag continues via pointer capture and
   * releasing over a target connects immediately), a static hold on a connected
   * pin fills the disconnect ring and severs the edge on completion (releasing
   * earlier cancels), and a plain tap falls through to onPinClick. A static
   * hold on an empty pin (or mid-pending) only suppresses the tap. The dot
   * holds the pointer capture for the whole drag, so the main ports must not
   * re-render mid-gesture (see startDragPending).
   */
  function attachDotHandlers(dot: HTMLElement, entityId: string, port: PortId, edge: Edge | null): void {
    let pressPointer: number | null = null;
    const press = { x: 0, y: 0 };
    let longFired = false;
    let dragWiring = false;
    let holdTimer: number | null = null;
    let ringEl: HTMLElement | null = null;
    let ringRaf: number | null = null;
    let ringStart = 0;

    function stopRing(): void {
      if (ringRaf !== null) {
        cancelAnimationFrame(ringRaf);
        ringRaf = null;
      }
      if (ringEl !== null) {
        ringEl.remove();
        ringEl = null;
      }
    }

    function clearHold(): void {
      if (holdTimer !== null) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      stopRing();
    }

    function tickRing(): void {
      if (ringEl === null) {
        return;
      }
      const t = Math.min((performance.now() - ringStart) / LONG_PRESS_MS, 1);
      ringEl.style.background = `conic-gradient(${FILL_RING_COLOR} ${t * 360}deg, transparent 0deg)`;
      if (t < 1) {
        ringRaf = requestAnimationFrame(tickRing);
      }
    }

    dot.addEventListener("pointerdown", (ev: PointerEvent): void => {
      ev.stopPropagation();
      longFired = false;
      dragWiring = false;
      pressPointer = ev.pointerId;
      press.x = ev.clientX;
      press.y = ev.clientY;
      dot.setPointerCapture(ev.pointerId);
      if (edge !== null && pendingConnection === null) {
        ringEl = document.createElement("span");
        ringEl.className = "fill-ring";
        dot.appendChild(ringEl);
        ringStart = performance.now();
        ringRaf = requestAnimationFrame(tickRing);
      }
      holdTimer = window.setTimeout((): void => {
        longFired = true;
        holdTimer = null;
        if (ringEl !== null) {
          stopRing();
          onPinLongPress(entityId, port, edge);
        }
      }, LONG_PRESS_MS);
    });
    dot.addEventListener("pointermove", (ev: PointerEvent): void => {
      if (pressPointer !== ev.pointerId) {
        return;
      }
      if (dragWiring) {
        updateWireHover(acceptableTargets(ev));
        return;
      }
      if (longFired || pendingConnection !== null) {
        return;
      }
      if (Math.hypot(ev.clientX - press.x, ev.clientY - press.y) < DRIFT_PX) {
        return;
      }
      clearHold();
      if (!canStartWireFrom(entityId, port)) {
        pressPointer = null;
        return;
      }
      dragWiring = true;
      startDragPending(entityId, port);
      updateWireHover(acceptableTargets(ev));
    });
    dot.addEventListener("pointerup", (ev: PointerEvent): void => {
      ev.stopPropagation();
      if (pressPointer !== ev.pointerId) {
        return;
      }
      pressPointer = null;
      clearHold();
      if (dragWiring) {
        dragWiring = false;
        dropPendingOn(acceptableTargets(ev), ev.clientX, ev.clientY);
        return;
      }
      if (!longFired) {
        onPinClick(entityId, port, edge);
      }
    });
    dot.addEventListener("pointercancel", (): void => {
      pressPointer = null;
      clearHold();
      if (dragWiring) {
        dragWiring = false;
        cancelPending();
      }
    });
    dot.addEventListener("pointerleave", (): void => {
      clearHold();
    });
  }

  /**
   * Param names already driven by setParamIn edges on `targetId`, optionally
   * excluding one edge so an edge's own binding is not counted against itself.
   * Unset (`""`) params are not collected — only committed bindings occupy a slot.
   */
  function boundParams(targetId: string, exceptEdgeId: string | null): Set<string> {
    const bound = new Set<string>();
    for (const e of graph.edgesTo(targetId, "setParamIn")) {
      if (e.id !== exceptEdgeId && e.param !== "") {
        bound.add(e.param);
      }
    }
    return bound;
  }

  /**
   * Display label for a param option, matching the object panel. Most params use
   * the shared localized paramLabel; `pitch` is the one param whose meaning shifts
   * with the target's timbre — a kit timbre selects a piece, so it reads as
   * "piece" there and "pitch" otherwise, exactly as the panel labels its control.
   */
  function paramOptionLabel(entityId: string, name: string): string {
    if (name !== "pitch") {
      return paramLabel(name);
    }
    const obj = field.list().find((o): boolean => o.id === entityId);
    return obj && isKitTimbre(obj.timbreId) ? t("param.piece") : t("param.pitch");
  }

  /**
   * The param dropdown shown beside a connected Param-in pin: it lists the
   * target's offered params (settable params whose manual UI is exposed under
   * the current feature toggles / emitter kind) minus those already bound by
   * another setParamIn edge on the same target, and writes the chosen name onto
   * the edge. An edge already bound to a filtered-out param keeps its own option
   * so the existing wiring stays visible and keeps driving. pointerdown is
   * stopped so opening it never starts a pin drag or canvas pick.
   */
  function makeParamSelect(entityId: string, edge: Edge): HTMLSelectElement {
    const select = document.createElement("select");
    select.className = "port-param";
    if (wireGuestLocked(entityId)) {
      select.disabled = true;
    }
    const taken = boundParams(entityId, edge.id);
    const names = offeredParams(entityId).filter((name: string): boolean => !taken.has(name));
    if (edge.param !== "" && !names.includes(edge.param) && settableParams(entityId).some((p: NumericParam): boolean => p.name === edge.param)) {
      names.push(edge.param);
    }
    for (const name of names) {
      const opt = document.createElement("option");
      opt.value = name;
      opt.textContent = paramOptionLabel(entityId, name);
      select.appendChild(opt);
    }
    select.value = names.includes(edge.param) ? edge.param : names[0] ?? "";
    select.addEventListener("pointerdown", (ev: PointerEvent): void => ev.stopPropagation());
    select.addEventListener("change", (): void => {
      pushUndo();
      graph.setEdgeParam(edge.id, select.value);
      persistConnections();
      bindPanel();
    });
    return select;
  }

  /**
   * The typed operand-2 field shown beside an unwired operand2In pin: clicking
   * opens the relative-log popover. pointerdown is stopped so editing never
   * starts a pin drag. reattachRelativeIfFor updates any open popover's span
   * reference when the panel is rebuilt after a commit.
   */
  function makeOperandField(entityId: string): HTMLSpanElement {
    const obj = field.list().find((o): boolean => o.id === entityId);
    const last = obj ? obj.operand : 0;
    const span = document.createElement("span");
    span.className = "port-operand-num";
    span.textContent = formatSig4(last);
    span.dataset.popoverKey = entityId;
    reattachRelativeIfFor(entityId, span, last);
    span.addEventListener("pointerdown", (ev: PointerEvent): void => ev.stopPropagation());
    span.addEventListener("click", (): void => {
      const cur = field.list().find((o): boolean => o.id === entityId);
      const curVal = cur ? cur.operand : last;
      openRelativePopover(
        span,
        curVal,
        (v: number): void => {
          span.textContent = formatSig4(v);
          const live = field.list().find((o): boolean => o.id === entityId);
          if (live && live.type === "calc") live.operand = v;
        },
        (v: number): void => {
          setNodeOperand(entityId, v);
        },
        span,
      );
    });
    return span;
  }

  /**
   * A connected pin is labelled by its far end's bare name. A setParamIn edge is
   * shown with its param dropdown instead (on either end), so this only labels
   * the other port kinds. Unconnected pins and pins wired to an unnamed node
   * keep their own port label.
   */
  function connectedLabel(port: PortId, edge: Edge): string {
    const def = PORTS[port];
    const farId = def.dir === "out" ? edge.to : edge.from;
    const farName = entityLabel(farId);
    return farName === "" ? PORT_LABELS[port] : farName;
  }

  const PULSE_GLYPH_PATH = "M4.76 2.96 L12.24 7.04 Q14 8 12.24 8.96 L4.76 13.04 Q3 14 3 12 L3 4 Q3 2 4.76 2.96 Z";

  /** A rounded right-pointing triangle for a pulse pin, drawn as SVG so its corners round and its fill toggles with the connected class (CSS clip-path can't round polygon corners). */
  function makePulseGlyph(): SVGSVGElement {
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("class", "port-pulse-glyph");
    svg.setAttribute("viewBox", "0 0 16 16");
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", PULSE_GLYPH_PATH);
    svg.appendChild(path);
    return svg;
  }

  function makeDotRow(entityId: string, port: PortId, edge: Edge | null, registry: Map<string, HTMLElement>): HTMLDivElement {
    const def = PORTS[port];
    const row = document.createElement("div");
    row.className = `port-row ${def.dir === "out" ? "port-out" : "port-in"}`;
    const dot = document.createElement("button");
    dot.className = `port-dot port-${def.type} port-name-${port}`;
    if (def.type === "pulse") {
      dot.appendChild(makePulseGlyph());
    }
    if (edge !== null) {
      dot.classList.add("connected");
    }
    if (edge !== null && (wireGuestLocked(edge.from) || wireGuestLocked(edge.to))) {
      dot.classList.add("wire-locked");
      dot.disabled = true;
    }
    if (pendingConnection !== null && pendingConnection.id === entityId && pendingConnection.port === port && edge === null) {
      dot.classList.add("pending");
    }
    attachDotHandlers(dot, entityId, port, edge);
    if (edge) {
      registry.set(def.dir === "out" ? edgeOutKey(edge) : edgeInKey(edge), dot);
    } else {
      registry.set(emptyKey(def.dir === "out" ? "o" : "i", entityId, port), dot);
    }
    const label = document.createElement("span");
    label.className = "port-label";
    label.textContent = edge !== null ? connectedLabel(port, edge) : portLabel(entityId, port);
    if (def.dir === "in") {
      if (port === "setParamIn" && edge) {
        row.append(dot, makeParamSelect(entityId, edge));
      } else if (port === "operand2In" && edge === null) {
        label.textContent = `${portLabel(entityId, port)}:`;
        row.append(dot, label, makeOperandField(entityId));
      } else {
        row.append(dot, label);
      }
    } else if (edge && edge.toPort === "setParamIn") {
      row.append(makeParamSelect(edge.to, edge), dot);
    } else {
      row.append(label, dot);
    }
    return row;
  }

  /**
   * Renders an entity's ports into a container. Every port follows the UE idiom —
   * one 1:1 dot per existing edge plus a trailing empty dot — except the
   * single-connection inputs (value-in / operand-in / transform-in), each a lone
   * dot with no growth.
   */
  function renderPortsInto(container: HTMLElement, entityId: string, registry: Map<string, HTMLElement>): void {
    for (const port of portsForEntity(entityId)) {
      if (pendingConnection !== null && entityId !== pendingConnection.id) {
        const pendingDef = PORTS[pendingConnection.port];
        const portDef = PORTS[port];
        if (portDef.dir === pendingDef.dir || portDef.type !== pendingDef.type) {
          continue;
        }
      }
      const def = PORTS[port];
      const edges = def.dir === "out"
        ? graph.edgesFrom(entityId).filter((e: Edge): boolean => e.fromPort === port)
        : graph.edgesTo(entityId, port);
      if (isSinglePort(port)) {
        container.appendChild(makeDotRow(entityId, port, edges[0] ?? null, registry));
        continue;
      }
      for (const e of edges) {
        container.appendChild(makeDotRow(entityId, port, e, registry));
      }
      if (port === "setParamIn" && !hasFreeParam(entityId)) {
        continue;
      }
      container.appendChild(makeDotRow(entityId, port, null, registry));
    }
  }

  /**
   * The group panel hides parent-child wiring and fully-internal members, showing
   * only the group's external interface: a member appears (under its own sub-header)
   * when all of its in ports or all of its out ports have no group-internal edge,
   * and only its ports without internal edges are listed. An unconnected operand2
   * constant always surfaces, even on otherwise fully-internal members — it is a
   * tweakable parameter of the group. Returns whether anything was rendered so the
   * caller can hide an empty section.
   */
  function renderGroupPorts(container: HTMLElement, groupId: string, registry: Map<string, HTMLElement>): boolean {
    const inside = groupInsideIds(groupId);
    const exprOrder = expressionMemberOrder(groupId, graph, field.list());
    const rank = new Map<string, number>(exprOrder.map((id: string, i: number): [string, number] => [id, i]));
    const members = [...inside].filter((id: string): boolean => id !== groupId);
    members.sort((a: string, b: string): number =>
      (rank.get(a) ?? exprOrder.length) - (rank.get(b) ?? exprOrder.length));
    let rendered = false;
    const ownRows: HTMLElement[] = [];
    for (const port of portsForEntity(groupId).filter((p: PortId): boolean => p !== "transformOut")) {
      if (pendingConnection !== null && groupId !== pendingConnection.id) {
        const pendingDef = PORTS[pendingConnection.port];
        const portDef = PORTS[port];
        if (portDef.dir === pendingDef.dir || portDef.type !== pendingDef.type) {
          continue;
        }
      }
      if (isSinglePort(port)) {
        ownRows.push(makeDotRow(groupId, port, graph.edgesTo(groupId, port)[0] ?? null, registry));
        continue;
      }
      for (const e of graph.edgesTo(groupId, port)) {
        ownRows.push(makeDotRow(groupId, port, e, registry));
      }
      if (port === "setParamIn" && !hasFreeParam(groupId)) {
        continue;
      }
      ownRows.push(makeDotRow(groupId, port, null, registry));
    }
    if (ownRows.length > 0) {
      const sub = document.createElement("div");
      sub.className = entityName(groupId) ? "ports-member-title named" : "ports-member-title";
      sub.textContent = entityLabel(groupId) || (NODE_TYPE_LABEL[entityType(groupId) ?? ""] ?? "Node");
      container.appendChild(sub);
      container.append(...ownRows);
      rendered = true;
    }
    for (const memberId of members) {
      const ports = portsForEntity(memberId)
        .filter((p: PortId): boolean => p !== "transformIn" && p !== "transformOut");
      if (ports.length === 0) {
        continue;
      }
      const portEdges = (port: PortId): Edge[] => PORTS[port].dir === "out"
        ? graph.edgesFrom(memberId).filter((e: Edge): boolean => e.fromPort === port)
        : graph.edgesTo(memberId, port);
      const hasInternal = (port: PortId): boolean =>
        portEdges(port).some((e: Edge): boolean => inside.has(PORTS[port].dir === "out" ? e.to : e.from));
      const ins = ports.filter((p: PortId): boolean => PORTS[p].dir === "in");
      const outs = ports.filter((p: PortId): boolean => PORTS[p].dir === "out");
      const qualifies = ins.every((p: PortId): boolean => !hasInternal(p))
        || outs.every((p: PortId): boolean => !hasInternal(p));
      const visiblePorts = qualifies
        ? ports.filter((p: PortId): boolean => !hasInternal(p))
        : ports.filter((p: PortId): boolean => p === "operand2In" && portEdges(p).length === 0);
      const rows: HTMLElement[] = [];
      for (const port of visiblePorts) {
        if (pendingConnection !== null && memberId !== pendingConnection.id) {
          const pendingDef = PORTS[pendingConnection.port];
          const portDef = PORTS[port];
          if (portDef.dir === pendingDef.dir || portDef.type !== pendingDef.type) {
            continue;
          }
        }
        const edges = portEdges(port);
        if (isSinglePort(port)) {
          rows.push(makeDotRow(memberId, port, edges[0] ?? null, registry));
          continue;
        }
        for (const e of edges) {
          rows.push(makeDotRow(memberId, port, e, registry));
        }
        if (port === "setParamIn" && !hasFreeParam(memberId)) {
          continue;
        }
        rows.push(makeDotRow(memberId, port, null, registry));
      }
      if (rows.length === 0) {
        continue;
      }
      const sub = document.createElement("div");
      sub.className = entityName(memberId) ? "ports-member-title named" : "ports-member-title";
      sub.textContent = entityLabel(memberId) || (NODE_TYPE_LABEL[entityType(memberId) ?? ""] ?? "Node");
      container.appendChild(sub);
      container.append(...rows);
      rendered = true;
    }
    return rendered;
  }

  function renderMainPorts(): void {
    portsOverlay.innerHTML = "";
    mainDots.clear();
    objectPanelEl?.classList.toggle("wiring", pendingConnection !== null);
    const obj = field.selectedObject();
    const em = emitterField.selectedEmitter();
    const entityId = obj ? obj.id : em ? em.id : null;
    if (!nodeUiVisible() || !entityId || (entityId !== null && (isClaimLocked(entityId) || wireGuestLocked(entityId))) || portsForEntity(entityId).length === 0) {
      anchorPortsOverlay();
      return;
    }
    if (obj && obj.type === "group") {
      appendPortColumnHeaders(portsOverlay);
      renderGroupPorts(portsOverlay, entityId, mainDots);
    } else {
      appendPortColumnHeaders(portsOverlay);
      renderPortsInto(portsOverlay, entityId, mainDots);
    }
    anchorPortsOverlay();
    portsOverlay.classList.toggle("scrollable", portsOverlay.scrollHeight > portsOverlay.clientHeight + 1);
  }

  /**
   * Anchors a ports overlay just below an entity's live screen projection: the
   * overlay is top-centered (CSS translate(-50%, 0)) so its pins stack downward
   * over the lower part of the object. When the entity owns the gizmo its top is
   * set to the projected bottom of the rotation ring lifted a few px so it
   * slightly overlaps the handle; otherwise it falls a fixed clearance below the
   * projection point. Hidden when nothing is rendered or the projection is
   * unavailable. Position-only — never rebuilds the dots, so a pin holding the
   * drag's pointer capture survives camera/object motion mid-wire.
   */
  function anchorOverlayBelow(el: HTMLElement, id: string, withGizmo: boolean): void {
    const proj = el.childElementCount > 0 ? projForNodeLive(id) : null;
    if (!proj) {
      el.style.display = "none";
      return;
    }
    el.style.display = "";
    let topY = proj.y + PORTS_OVERLAY_BASE_CLEARANCE_PX;
    if (withGizmo && gizmo.hasTarget()) {
      const ringBottom = projForNodeLiveRaised(id, -gizmo.ringRadius());
      if (ringBottom) {
        topY = ringBottom.y - PORTS_OVERLAY_RING_OVERLAP_PX;
      }
    }
    el.style.left = `${proj.x}px`;
    el.style.top = `${topY}px`;
  }

  function anchorPortsOverlay(): void {
    const sel = selectedEntityId();
    if (!sel) {
      portsOverlay.style.display = "none";
      return;
    }
    anchorOverlayBelow(portsOverlay, sel, true);
  }

  /**
   * Reconciles the on-object pin-chooser sub-panel with the focused wire target:
   * a pending wire that has focused a target (drop landing on a multi-pin target,
   * or a canvas tap cycling an overlapping cluster) renders that target's
   * compatible pins below it so one tap completes the wire; cycling the focus
   * moves the single sub-panel to the next target. Rebuilt only when the focused
   * id changes (re-rendering mid-tap would drop the pin's pointer). Anchored every
   * frame via anchorSubOverlay.
   */
  function renderSubPanel(id: string | null): void {
    subOverlay.innerHTML = "";
    subDots.clear();
    if (id === null || pendingConnection === null) {
      subOverlay.style.display = "none";
      return;
    }
    renderPortsInto(subOverlay, id, subDots);
    anchorSubOverlay();
  }

  function anchorSubOverlay(): void {
    if (pendingConnection === null || subPanelId === null) {
      subOverlay.style.display = "none";
      return;
    }
    anchorOverlayBelow(subOverlay, subPanelId, false);
  }

  function selectedEntityId(): string | null {
    return field.selectedObject()?.id ?? emitterField.selectedEmitter()?.id ?? null;
  }

  /**
   * Whether a settable param remains unbound on the target. A second value can
   * only land on setParamIn when there is still a param for it to drive, so a
   * fully-bound target stops offering the port (no silent duplicate binding).
   */
  function hasFreeParam(targetId: string): boolean {
    const taken = boundParams(targetId, null);
    return offeredParams(targetId).some((name: string): boolean => !taken.has(name));
  }

  /**
   * Whether the target node has any port that could accept the pending wire: a
   * complementary-direction port of the same signal type with a free pin (outputs
   * and growable inputs always have an empty pin; single value-in only when
   * unwired; setParamIn only while a settable param is still unbound).
   */
  function compatiblePendingPorts(targetId: string): PortId[] {
    if (!pendingConnection) {
      return [];
    }
    if (wireGuestLocked(targetId)) {
      return [];
    }
    const pendingDef = PORTS[pendingConnection.port];
    const wantDir = pendingDef.dir === "out" ? "in" : "out";
    const ports: PortId[] = [];
    for (const p of portsForEntity(targetId)) {
      const def = PORTS[p];
      if (def.dir !== wantDir || def.type !== pendingDef.type) {
        continue;
      }
      if (p === "setParamIn" && !hasFreeParam(targetId)) {
        continue;
      }
      if (def.dir === "out" || !isSinglePort(p) || graph.edgesTo(targetId, p).length === 0) {
        ports.push(p);
      }
    }
    return ports;
  }

  /**
   * Whether the target can accept the pending wire on a free pin. A group is never
   * a direct wire target: during wiring its pick box yields no hit, so a wire
   * dropped over a group lands on the member under the cursor rather than the
   * group's aggregate. The group's aggregate setParamIn is still wired through its
   * explicit pin on the group panel (a DOM dot, not a canvas body hit).
   */
  function canTargetAcceptPending(targetId: string): boolean {
    if (entityType(targetId) === "group") {
      return false;
    }
    return compatiblePendingPorts(targetId).length > 0;
  }

  /**
   * Creatable tools offered as new-object candidates when a wire is released over
   * empty space. Rail and group are excluded — both are formed by a multi-point /
   * marquee gesture, not a single drop point, so they have no meaningful
   * point-placement. The two emitter kinds stay distinct (Sequencer vs Stream).
   */
  const CONNECT_CANDIDATE_TOOLS: readonly Tool[] = [
    "box", "sphere", "funnel", "force",
    "calc", "count", "display", "timer", "decay", "delay", "router",
    "button", "lamp", "knob", "envelope", "array", "curve",
    "timbre", "pitchQuantize", "orbit",
    "emitter-sync", "emitter-full",
  ];

  const CONNECT_TOOL_LABEL: Record<Tool, MessageKey> = {
    box: "tool.box",
    sphere: "tool.sphere",
    funnel: "tool.funnel",
    rail: "tool.rail",
    force: "tool.force",
    orbit: "tool.orbit",
    group: "tool.group",
    calc: "tool.calc",
    count: "tool.count",
    display: "tool.display",
    timer: "tool.timer",
    decay: "tool.decay",
    delay: "tool.delay",
    router: "tool.router",
    button: "tool.button",
    lamp: "tool.lamp",
    knob: "tool.knob",
    envelope: "tool.envelope",
    array: "tool.array",
    curve: "tool.curve",
    timbre: "tool.timbre",
    pitchQuantize: "tool.pitchQuantize",
    global: "tool.global",
    "emitter-sync": "tool.sequencer",
    "emitter-full": "tool.stream",
  };

  function portsForTool(tool: Tool): PortId[] {
    if (tool === "emitter-sync" || tool === "emitter-full") {
      return portsForType("emitter");
    }
    return portsForType(tool);
  }

  /**
   * Whether a tool is creatable under the current feature toggles, mirroring the
   * palette's per-section gating: funnel needs the 3D feature and force needs the
   * physics feature, so neither is offered as a candidate when its feature is off.
   * Every other candidate tool is a node / transform / shape / emitter that is
   * already reachable whenever the wiring UI is shown (node feature on).
   */
  function toolEnabled(tool: Tool): boolean {
    if (isNodeGatedTool(tool) && (settings.simpleMode || !settings.features.node)) {
      return false;
    }
    if (tool === "funnel") {
      return settings.features["3d"];
    }
    if (tool === "force") {
      return settings.features.physics;
    }
    return true;
  }

  /**
   * Tools whose freshly-created entity would expose a port complementary to the
   * pending wire (opposite direction, same signal type). A new entity has every
   * pin free, so only direction + signal type gate the match here — the
   * single-connection / free-param constraints that compatiblePendingPorts applies
   * to existing instances are always satisfied on a fresh one.
   */
  function candidateToolsForPending(): Tool[] {
    if (!pendingConnection) {
      return [];
    }
    const pendingDef = PORTS[pendingConnection.port];
    const wantDir = pendingDef.dir === "out" ? "in" : "out";
    const result: Tool[] = [];
    for (const tool of CONNECT_CANDIDATE_TOOLS) {
      if (!toolEnabled(tool)) {
        continue;
      }
      if (portsForTool(tool).some((p: PortId): boolean => PORTS[p].dir === wantDir && PORTS[p].type === pendingDef.type)) {
        result.push(tool);
      }
    }
    return result;
  }

  /**
   * Per-selection memo of the graph queries the overlay re-runs every frame
   * (far id → palette color, selected entity's edges, and a group's boundary
   * edges). Keyed by selection id + graph revision so any wire add/remove/param
   * edit or a selection change recomputes lazily on next read; `groupBoundaryEdges`
   * is only filled for a selected group.
   */
  type SelectionCache = {
    selId: string;
    rev: number;
    selEdges: Edge[];
    boundaryEdges: Edge[] | null;
  };
  let selectionCache: SelectionCache | null = null;

  function selectionData(): SelectionCache | null {
    const sel = selectedEntityId();
    if (!sel) {
      selectionCache = null;
      return null;
    }
    const rev = graph.version();
    if (selectionCache && selectionCache.selId === sel && selectionCache.rev === rev) {
      return selectionCache;
    }
    const selIsGroup = entityType(sel) === "group";
    selectionCache = {
      selId: sel,
      rev,
      selEdges: graph.edgesForEntity(sel),
      boundaryEdges: selIsGroup ? groupBoundaryEdges(sel) : null,
    };
    return selectionCache;
  }

  /**
   * Shows the current wire target as an OutlinePass halo (focus wins over hover,
   * deduped) and the pin-chooser sub-panel for the focused target only (hover
   * never opens it). Cycling the focus rebuilds the sub-panel on the next target;
   * the rebuild is gated on the focused id changing so a tap mid-selection keeps
   * its pointer.
   */
  function refreshWireDisplay(): void {
    const halo = wireFocusId ?? wireHoverId;
    if (halo !== wireDisplayId) {
      wireDisplayId = halo;
      highlightWireTarget(halo);
    }
    if (wireFocusId !== subPanelId) {
      subPanelId = wireFocusId;
      renderSubPanel(subPanelId);
    }
  }

  /**
   * Resets every wiring-mode bit of state — pending source, focus/hover target,
   * the displayed halo and the pin-chooser sub-panel — so confirm / cancel /
   * empty-click all converge on one clean slate. Re-renders the main ports
   * without the pending pin.
   */
  function teardownWiring(): void {
    closeConnectMenu();
    pendingConnection = null;
    wireFocusId = null;
    wireHoverId = null;
    wireDisplayId = null;
    subPanelId = null;
    renderSubPanel(null);
    highlightWireTarget(null);
    renderMainPorts();
  }

  function cancelPending(): void {
    teardownWiring();
  }

  function startPending(entityId: string, port: PortId, edge: Edge | null): void {
    if (edge !== null) {
      return;
    }
    pendingConnection = { id: entityId, port };
    wireFocusId = null;
    wireHoverId = null;
    wireDisplayId = null;
    highlightWireTarget(null);
    renderMainPorts();
  }

  /**
   * Begins a pending wire mid-press (drift off a port dot). Unlike startPending
   * it must not re-render the main ports: the pressed dot holds the pointer
   * capture for the rest of the drag, and a re-render would remove it from the
   * DOM and drop the capture (killing the gesture on touch).
   */
  function startDragPending(entityId: string, port: PortId): void {
    pendingConnection = { id: entityId, port };
    wireFocusId = null;
    wireHoverId = null;
    wireDisplayId = null;
    highlightWireTarget(null);
    objectPanelEl?.classList.add("wiring");
  }

  /**
   * The single immediate-wiring judgment shared by the drag-release and the
   * canvas-tap paths: a pending wire lands without further interaction ONLY
   * when both the target and its pin are unambiguous — exactly one acceptable
   * target offering exactly one compatible pin. A multi-pin target keeps the
   * wire pending with the target focused and haloed so a follow-up tap resolves
   * the pin; an overlapping cluster cycles the focus instead. Returns
   * whether there was anything to land on (false = caller decides the miss).
   * Auto-committing the first pin of a multi-pin target is how a value drop
   * once silently landed on the wrong emitter input, so every release path
   * must run through this judgment rather than picking a pin itself.
   */
  function settlePending(acceptableIds: string[]): boolean {
    if (!pendingConnection || acceptableIds.length === 0) {
      return false;
    }
    if (acceptableIds.length === 1) {
      const targetId = acceptableIds[0];
      const compatible = compatiblePendingPorts(targetId);
      if (compatible.length === 1) {
        commitConnection(targetId, compatible[0]);
        return true;
      }
      focusWireTarget(targetId);
      return true;
    }
    cycleWireFocus(acceptableIds);
    return true;
  }

  /**
   * Releases a dragged wire through the shared judgment. A miss (no acceptable
   * existing target) no longer cancels outright: it offers the new-object
   * candidates compatible with the pending port — none cancels, exactly one
   * creates + wires immediately at the drop point, and two or more open the
   * drop-anchored chooser.
   */
  function dropPendingOn(acceptableIds: string[], dropX: number, dropY: number): void {
    if (!pendingConnection) {
      return;
    }
    if (settlePending(acceptableIds)) {
      return;
    }
    const candidates = candidateToolsForPending();
    if (candidates.length === 0) {
      teardownWiring();
      return;
    }
    if (candidates.length === 1) {
      createAndWire(candidates[0], dropX, dropY);
      return;
    }
    showConnectMenu(candidates, dropX, dropY);
  }

  let connectMenu: HTMLDivElement | null = null;

  function onConnectMenuOutside(ev: PointerEvent): void {
    if (connectMenu !== null && !connectMenu.contains(ev.target as Node)) {
      teardownWiring();
    }
  }

  function onConnectMenuKey(ev: KeyboardEvent): void {
    if (ev.key === "Escape") {
      teardownWiring();
    }
  }

  function closeConnectMenu(): void {
    if (connectMenu === null) {
      return;
    }
    connectMenu.remove();
    connectMenu = null;
    window.removeEventListener("pointerdown", onConnectMenuOutside, true);
    window.removeEventListener("keydown", onConnectMenuKey, true);
  }

  /**
   * The drop-anchored new-object chooser: one button per candidate tool, top-left
   * pinned at the drop point. Selecting a button creates + wires that tool; a
   * pointerdown outside or Escape cancels the whole pending wire. The capture-phase
   * outside listener is registered after this turn's pointerup so the release that
   * opened the menu never dismisses it.
   */
  function showConnectMenu(tools: Tool[], dropX: number, dropY: number): void {
    closeConnectMenu();
    const menu = document.createElement("div");
    menu.className = "connect-menu";
    const heading = document.createElement("div");
    heading.className = "connect-menu-heading";
    heading.textContent = t("portConnect.newObject");
    menu.appendChild(heading);
    for (const tool of tools) {
      const btn = document.createElement("button");
      btn.className = "connect-menu-item";
      btn.textContent = t(CONNECT_TOOL_LABEL[tool]);
      btn.addEventListener("pointerdown", (ev: PointerEvent): void => ev.stopPropagation());
      btn.addEventListener("click", (): void => {
        const x = dropX;
        const y = dropY;
        closeConnectMenu();
        createAndWire(tool, x, y);
      });
      menu.appendChild(btn);
    }
    menu.style.left = `${dropX}px`;
    menu.style.top = `${dropY}px`;
    document.body.appendChild(menu);
    connectMenu = menu;
    window.addEventListener("pointerdown", onConnectMenuOutside, true);
    window.addEventListener("keydown", onConnectMenuKey, true);
  }

  /**
   * Creates a new entity of `tool` at the drop point and wires the pending port to
   * its first compatible free pin (portsForEntity enumeration order). The whole
   * operation is one undo step: the snapshot is taken before creation so a single
   * undo removes both the new entity and the wire. A creation failure rolls the
   * snapshot back; a wiring failure leaves the (undoable) entity in place.
   */
  function createAndWire(tool: Tool, dropX: number, dropY: number): void {
    if (!pendingConnection) {
      return;
    }
    const pending = pendingConnection;
    pushUndo();
    const newId = createEntityAt(tool, dropX, dropY);
    if (newId === null) {
      popUndo();
      teardownWiring();
      return;
    }
    const compatible = compatiblePendingPorts(newId);
    if (compatible.length === 0) {
      teardownWiring();
      return;
    }
    const targetPort = compatible[0];
    const pendingDef = PORTS[pending.port];
    const out = pendingDef.dir === "out" ? pending : { id: newId, port: targetPort };
    const inn = pendingDef.dir === "in" ? pending : { id: newId, port: targetPort };
    if (graph.connect({ from: out.id, fromPort: out.port, to: inn.id, toPort: inn.port })) {
      if (inn.port === "setParamIn") {
        defaultParamEdge(out.id, inn.id);
      }
      persistConnections();
      onConnect(inn.port);
    }
    teardownWiring();
    bindPanel();
  }

  /**
   * A pin tap on an empty pin either starts a pending connection (from either an
   * output or an input — drag from either end), completes one when it is the
   * complementary direction, re-arms when it is the same direction, or toggles
   * off when it is the empty pin already pending. Taps on an occupied (1:1) pin
   * are ignored — disconnect is long-press. The out side becomes the edge `from`.
   */
  /**
   * Seeds a freshly created Param-in edge's param, preferring `pitch` when the
   * target offers it (else the first offered name), provided it is not
   * already driven by another Param-in edge, so wiring a second value onto the
   * same object lands on a free param instead of fighting the existing
   * wire (the inline dropdown then lets the user pick another). The connection
   * guard refuses a wire once every param is bound, so a fully-bound target never
   * reaches here — no free name means early return rather than a duplicate
   * binding. The new edge is the latest match with an unset param.
   */
  function defaultParamEdge(fromId: string, toId: string): void {
    const offered = offeredParams(toId);
    if (offered.length === 0) {
      return;
    }
    const candidates = graph.edgesTo(toId, "setParamIn").filter((e: Edge): boolean => e.from === fromId && e.param === "");
    const edge = candidates[candidates.length - 1];
    if (!edge) {
      return;
    }
    const taken = boundParams(toId, edge.id);
    const free = offered.includes("pitch") && !taken.has("pitch") ? "pitch" : offered.find((name: string): boolean => !taken.has(name));
    if (!free) {
      return;
    }
    graph.setEdgeParam(edge.id, free);
  }

  function selectEntityById(id: string): boolean {
    for (const o of field.list()) {
      if (o.id === id) {
        field.select(o);
        emitterField.deselect();
        return true;
      }
    }
    for (const e of emitterField.list()) {
      if (e.id === id) {
        emitterField.select(e);
        field.deselect();
        return true;
      }
    }
    return false;
  }

  function onPinClick(entityId: string, port: PortId, edge: Edge | null): void {
    const def = PORTS[port];
    if (!pendingConnection) {
      if (edge !== null) {
        const farId = def.dir === "out" ? edge.to : edge.from;
        if (selectEntityById(farId)) {
          bindPanel();
        }
        return;
      }
      startPending(entityId, port, edge);
      return;
    }
    if (pendingConnection.id === entityId && pendingConnection.port === port && edge === null) {
      cancelPending();
      return;
    }
    if (edge !== null) {
      return;
    }
    const pendingDef = PORTS[pendingConnection.port];
    if (pendingDef.dir === def.dir) {
      startPending(entityId, port, edge);
      return;
    }
    commitConnection(entityId, port);
  }

  /**
   * Establishes the pending wire onto the given target pin: the out side becomes the
   * edge `from`, a fresh Param-in edge is seeded, and the connection is persisted.
   * Shared by the settle judgment and the canvas immediate-connect path so undo /
   * persist / state reset stay identical. Tears down all wiring state.
   */
  function commitConnection(targetId: string, targetPort: PortId): void {
    if (!pendingConnection) {
      return;
    }
    const pendingDef = PORTS[pendingConnection.port];
    const targetDef = PORTS[targetPort];
    if (pendingDef.dir === targetDef.dir) {
      return;
    }
    const out = pendingDef.dir === "out" ? pendingConnection : { id: targetId, port: targetPort };
    const inn = pendingDef.dir === "in" ? pendingConnection : { id: targetId, port: targetPort };
    if (wireGuestLocked(out.id) || wireGuestLocked(inn.id)) {
      teardownWiring();
      bindPanel();
      return;
    }
    if (inn.port === "setParamIn" && !hasFreeParam(inn.id)) {
      teardownWiring();
      bindPanel();
      return;
    }
    const candidate = { from: out.id, fromPort: out.port, to: inn.id, toPort: inn.port };
    pushUndo();
    if (graph.connect(candidate)) {
      if (inn.port === "setParamIn") {
        defaultParamEdge(out.id, inn.id);
      }
      persistConnections();
      onConnect(inn.port);
    } else {
      popUndo();
    }
    teardownWiring();
    bindPanel();
  }

  function onPinLongPress(_entityId: string, _port: PortId, edge: Edge | null): void {
    if (!edge) {
      return;
    }
    if (wireGuestLocked(edge.from) || wireGuestLocked(edge.to)) {
      return;
    }
    pushUndo();
    graph.disconnect(edge);
    persistConnections();
    teardownWiring();
    bindPanel();
  }

  /**
   * Cubic-bezier path with the output exiting to the right and the input
   * entering from the left (control points pushed ±K horizontally), matching the
   * node-graph idiom regardless of the relative screen positions. An end may
   * carry its own control point (cx/cy) to override the horizontal tangent.
   */
  function bezierPath(a: LineEnd, b: LineEnd): string {
    const k = Math.max(40, Math.abs(b.x - a.x) * 0.5);
    return `M ${a.x} ${a.y} C ${a.cx ?? a.x + k} ${a.cy ?? a.y}, ${b.cx ?? b.x - k} ${b.cy ?? b.y}, ${b.x} ${b.y}`;
  }

  const OBJECT_ENTRY_OFFSET_PX = 20;
  const OBJECT_ENTRY_CTRL_PX = 80;

  /**
   * Line terminus at a scene object for the far end of a connection that has no
   * on-screen dot. Both ends are corrected horizontally toward the curve's other
   * end (`towardX`): the touch point and its control handle sit on the side
   * facing the opposite terminus, so the wire leaves/enters along the shortest
   * horizontal path instead of always bowing the same way. The touch point is
   * pulled back 20px so the object's center label stays readable.
   */
  function objectEntryAnchor(proj: { x: number; y: number }, towardX: number): ObjectEntry {
    const ux = towardX >= proj.x ? 1 : -1;
    return {
      x: proj.x + ux * OBJECT_ENTRY_OFFSET_PX,
      y: proj.y,
      cx: proj.x + ux * (OBJECT_ENTRY_OFFSET_PX + OBJECT_ENTRY_CTRL_PX),
      cy: proj.y,
    };
  }

  const overlayPaths: SVGPathElement[] = [];

  /**
   * Reconciles the overlay's `<path>` children in place: existing elements are
   * reused by index with their attributes overwritten, the tail is appended /
   * trimmed to match the count. Rebuilding from scratch every frame thrashed
   * layout; in-place updates keep the wire count steady. stroke must be cleared
   * (not left stale) when null so the pending wire falls back to the CSS stroke.
   */
  function setOverlayPaths(paths: { d: string; stroke: string | null }[]): void {
    for (let i = 0; i < paths.length; i++) {
      const p = paths[i];
      let path = overlayPaths[i];
      if (!path) {
        path = document.createElementNS(SVG_NS, "path");
        overlay.appendChild(path);
        overlayPaths[i] = path;
      }
      path.setAttribute("d", p.d);
      path.style.stroke = p.stroke ?? "";
    }
    while (overlayPaths.length > paths.length) {
      overlayPaths.pop()!.remove();
    }
  }

  /**
   * Connection curves are drawn while a connection is pending (source out-dot →
   * cursor) or whenever an entity is selected (out-dot → in-dot per edge, each
   * stroked with its source port's signal-type color so the wire matches the dot
   * it leaves, falling back to a projected node entry anchor when a dot isn't on
   * screen — the far end has no on-object dot). Group↔member parent edges
   * (transformOut) are skipped here; a selected group still draws its boundary
   * wires (which never include transform self-parent edges). Re-anchors the
   * on-object ports overlay and the pin-chooser sub-panel each frame so they
   * track camera/node motion.
   */
  function updateConnectionOverlay(): void {
    if (!nodeUiVisible()) {
      setOverlayPaths([]);
      portsOverlay.style.display = "none";
      subOverlay.style.display = "none";
      return;
    }
    anchorPortsOverlay();
    anchorSubOverlay();
    const paths: { d: string; stroke: string | null }[] = [];
    if (pendingConnection) {
      const pendingDef = PORTS[pendingConnection.port];
      if (pendingDef.dir === "out") {
        const a = dotAnchor(emptyKey("o", pendingConnection.id, pendingConnection.port), "r") ?? projForNodeLive(pendingConnection.id);
        if (a) {
          paths.push({ d: bezierPath(a, cursor), stroke: null });
        }
      } else {
        const a = dotAnchor(emptyKey("i", pendingConnection.id, pendingConnection.port), "l") ?? projForNodeLive(pendingConnection.id);
        if (a) {
          paths.push({ d: bezierPath(cursor, a), stroke: null });
        }
      }
    } else {
      const selId = selectedEntityId();
      if (selId !== null && isClaimLocked(selId)) {
        setOverlayPaths([]);
        return;
      }
      const cache = selectionData();
      if (cache) {
        const edges = cache.boundaryEdges ?? cache.selEdges;
        for (let i = 0; i < edges.length; i++) {
          const e = edges[i];
          if (e.fromPort === "transformOut") {
            continue;
          }
          const oDot = dotAnchor(edgeOutKey(e), "r");
          const iDot = dotAnchor(edgeInKey(e), "l");
          const oProj = oDot ? null : projForNodeLive(e.from);
          const iProj = iDot ? null : projForNodeLive(e.to);
          const oRef = oDot ?? oProj;
          const iRef = iDot ?? iProj;
          if (!oRef || !iRef) {
            continue;
          }
          const oa = oDot ?? (oProj ? objectEntryAnchor(oProj, iRef.x) : null);
          const ia = iDot ?? (iProj ? objectEntryAnchor(iProj, oRef.x) : null);
          if (oa && ia) {
            const stroke = PORT_TYPE_COLOR[PORTS[e.fromPort].type];
            paths.push({ d: bezierPath(oa, ia), stroke });
          }
        }
      }
    }
    setOverlayPaths(paths);
  }

  window.addEventListener("pointermove", (ev: PointerEvent): void => {
    cursor.x = ev.clientX;
    cursor.y = ev.clientY;
  });

  /**
   * Pending-mode hover from the canvas pointer move: the front acceptable target
   * becomes the hover candidate. A click-established focus is never dropped by
   * hover — the pin-chooser panel a click opened stays put until the next click
   * re-focuses (settlePending / cycleWireFocus) or an empty click cancels, so
   * moving the pointer off the object to reach the panel can't dismiss it. The
   * display tracks focus ?? hover.
   */
  function updateWireHover(acceptableIds: string[]): void {
    wireHoverId = acceptableIds[0] ?? null;
    refreshWireDisplay();
  }

  function focusWireTarget(targetId: string): void {
    wireFocusId = targetId;
    refreshWireDisplay();
  }

  /**
   * Advances the focus through an overlapping cluster of acceptable targets: each
   * click steps front → next → … → last → front (no deselect step, unlike canvas
   * cycle-select). With no current focus in the cluster it lands on the front.
   */
  function cycleWireFocus(acceptableIds: string[]): void {
    if (acceptableIds.length === 0) {
      return;
    }
    const cur = wireFocusId !== null ? acceptableIds.indexOf(wireFocusId) : -1;
    wireFocusId = acceptableIds[cur < 0 ? 0 : (cur + 1) % acceptableIds.length];
    refreshWireDisplay();
  }

  function pendingId(): string | null {
    return pendingConnection ? pendingConnection.id : null;
  }

  function pendingPort(): PortId | null {
    return pendingConnection ? pendingConnection.port : null;
  }

  function isPending(): boolean {
    return pendingConnection !== null;
  }

  return {
    renderMainPorts,
    updateConnectionOverlay,
    cancelPending,
    canTargetAcceptPending,
    updateWireHover,
    focusWireTarget,
    settlePending,
    pendingId,
    pendingPort,
    isPending,
  };
}
